import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router } from 'react-router-dom';
import ConvexErrorBoundary from '../components/Layout/ConvexErrorBoundary';
import { DowntimeProvider, useDowntime } from '../contexts/DowntimeContext';
import { CoreProviders } from './CoreProviders';
import { UIProviders } from './UIProviders';
import { ChatProviders } from './ChatProviders';
import { FeatureProviders } from './FeatureProviders';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnMount: true, // Ensure data is fresh on mount
    },
  },
});

interface AppProvidersProps {
  children: React.ReactNode;
}

// Component that can access downtime context and pass it to ConvexErrorBoundary
const ConvexErrorBoundaryWithDowntime: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { downtime } = useDowntime();
  
  return (
    <ConvexErrorBoundary isDowntime={downtime.isDowntime}>
      {children}
    </ConvexErrorBoundary>
  );
};

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  return (
    <HelmetProvider>
      <DowntimeProvider>
        <ConvexErrorBoundaryWithDowntime>
            <QueryClientProvider client={queryClient}>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <CoreProviders>
                  <UIProviders>
                    <ChatProviders>
                      <FeatureProviders>
                        {children}
                      </FeatureProviders>
                    </ChatProviders>
                  </UIProviders>
                </CoreProviders>
              </Router>
            </QueryClientProvider>
        </ConvexErrorBoundaryWithDowntime>
      </DowntimeProvider>
    </HelmetProvider>
  );
};
